import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic page titles and meta tags
 * @param {string} title - Page title (will be appended with " | Strider")
 * @param {string} description - Meta description
 * @param {string} keywords - Additional keywords (comma-separated)
 * @param {string} canonical - Canonical URL path (e.g., "/browse")
 * @param {string} type - og:type (default: "website")
 * @param {string} image - OG image URL
 */
export default function SEO({ 
  title, 
  description, 
  keywords = "",
  canonical = "",
  type = "website",
  image = "/strider-logo.png"
}) {
  const siteTitle = "Strider";
  const fullTitle = title ? `${title} | ${siteTitle}` : `Strider | Find Verified Remote Workspaces in UK`;
  const baseUrl = "https://strider.work";
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  
  const defaultDescription = "Discover verified cafes, hotels, libraries and coworking spaces with measured WiFi speeds and power outlets for remote work across the UK. Find your perfect workspace today.";
  const metaDescription = description || defaultDescription;
  
  const defaultKeywords = "remote work, coworking space, work from cafe, laptop friendly cafe, wifi cafe UK, remote workspace, digital nomad UK, work from hotel, freelancer workspace, verified workspace, strider";
  const metaKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Strider" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={metaDescription} />
      <meta property="twitter:image" content={image} />
    </Helmet>
  );
}

/**
 * Generate structured data for a venue (Local Business schema)
 */
export function VenueStructuredData({ venue }) {
  if (!venue) return null;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": venue.name,
    "description": venue.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": venue.location?.address,
      "addressLocality": venue.location?.city,
      "postalCode": venue.location?.postcode,
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": venue.location?.lat,
      "longitude": venue.location?.lng
    },
    "url": `https://strider.work/venue/${venue.id}`,
    "image": venue.images?.[0],
    "aggregateRating": venue.review_count > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": venue.rating,
      "reviewCount": venue.review_count,
      "bestRating": "10",
      "worstRating": "1"
    } : undefined,
    "amenityFeature": [
      {
        "@type": "LocationFeatureSpecification",
        "name": "WiFi",
        "value": venue.amenities?.wifi_quality
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Power Outlets",
        "value": venue.amenities?.power_outlets > 0 ? "Available" : "Limited"
      }
    ],
    "priceRange": getPriceRange(venue.price_range?.coffee_avg)
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

function getPriceRange(priceLevel) {
  if (!priceLevel) return "££";
  if (priceLevel <= 2) return "£";
  if (priceLevel <= 3) return "££";
  if (priceLevel <= 4) return "£££";
  return "££££";
}

/**
 * Generate BreadcrumbList structured data
 */
export function BreadcrumbStructuredData({ items }) {
  if (!items || items.length === 0) return null;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://strider.work${item.path}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}
